import { useRef, useState } from 'react';
import { DeleteOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Alert, App, Button, DatePicker, Drawer, Form, Input, Modal, Popconfirm, Select, Table, Tag } from 'antd';
import { api } from '../api';
import { DataTable, ErrorNotice, Panel } from '../components';
import { formatDate, formatDateTime, useLoad } from '../lib';
const TAXABILITY = { taxable: 'Taxable', exempt: 'Exempt', nil_rated: 'Nil rated', non_gst: 'Non-GST' };
const KIND_OPTIONS = [
    { value: 'goods', label: 'Goods (HSN)' },
    { value: 'services', label: 'Services (SAC)' }
];
function rateLabel(rate) {
    if (!rate)
        return 'No rate in force';
    if (rate.taxability !== 'taxable')
        return TAXABILITY[rate.taxability];
    return `${rate.gstRate}%${rate.cessRate && rate.cessRate !== '0' ? ` + cess ${rate.cessRate}%` : ''}`;
}
const errorText = (err, fallback) => (err instanceof Error ? err.message : fallback);
export function HsnTab() {
    const { message } = App.useApp();
    const [query, setQuery] = useState('');
    const [adding, setAdding] = useState(false);
    const [viewing, setViewing] = useState(null);
    const [codeForm] = Form.useForm();
    const [rateForm] = Form.useForm();
    const [busy, setBusy] = useState(false);
    const list = useLoad(() => api(`/v1/admin/gst/hsn?q=${encodeURIComponent(query)}&limit=300`), [query]);
    const detail = useLoad(() => (viewing ? api(`/v1/admin/gst/hsn/${viewing}`).then((body) => body.entry) : Promise.resolve(null)), [viewing]);
    const taxability = Form.useWatch('taxability', rateForm);
    const fileInput = useRef(null);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState(null);
    async function saveCode(values) {
        setBusy(true);
        try {
            const body = await api('/v1/admin/gst/hsn', { method: 'POST', body: JSON.stringify(values) });
            message.success(`${body.entry.code} saved; add its rate next`);
            setAdding(false);
            await list.reload();
            setViewing(body.entry.code);
        }
        catch (err) {
            message.error(errorText(err, 'Could not save the code'));
        }
        finally {
            setBusy(false);
        }
    }
    async function saveRate(values) {
        if (!viewing)
            return;
        setBusy(true);
        try {
            await api(`/v1/admin/gst/hsn/${viewing}/rates`, { method: 'POST', body: JSON.stringify({ ...values, fromDate: values.fromDate.format('YYYY-MM-DD') }) });
            message.success('Rate saved');
            rateForm.resetFields();
            await Promise.all([detail.reload(), list.reload()]);
        }
        catch (err) {
            message.error(errorText(err, 'Could not save the rate'));
        }
        finally {
            setBusy(false);
        }
    }
    async function removeRate(rate) {
        try {
            await api(`/v1/admin/gst/hsn/${rate.code}/rates/${rate.fromDate}`, { method: 'DELETE' });
            message.success('Rate removed');
            await Promise.all([detail.reload(), list.reload()]);
        }
        catch (err) {
            message.error(errorText(err, 'Could not remove the rate'));
        }
    }
    async function pick(chosen) {
        if (!chosen)
            return;
        if (chosen.size > 4 * 1024 * 1024)
            return message.error('The file is over 4 MB. Split it into smaller files.');
        const csv = await chosen.text();
        setFile({ name: chosen.name, csv });
        try {
            setPreview(await api('/v1/admin/gst/hsn/import', { method: 'POST', body: JSON.stringify({ fileName: chosen.name, csv, preview: true }) }));
        }
        catch (err) {
            setPreview(null);
            message.error(errorText(err, 'Could not read the file'));
        }
    }
    async function runImport() {
        if (!file)
            return;
        setBusy(true);
        try {
            const body = await api('/v1/admin/gst/hsn/import', { method: 'POST', body: JSON.stringify({ fileName: file.name, csv: file.csv, preview: false }) });
            message.success(`${body.accepted} rates imported; ${body.rejected} rows left out`);
            setFile(null);
            setPreview(null);
            await list.reload();
        }
        catch (err) {
            message.error(errorText(err, 'Could not import the file'));
        }
        finally {
            setBusy(false);
        }
    }
    const entry = detail.data;
    return (<>
      {list.data && list.data.total === 0 && !query && (<Alert type="info" showIcon style={{ marginBottom: 16 }} title="The HSN/SAC master is empty." description="Add codes from the official rate schedules (CBIC rate notifications), or import a CSV made from them. Every rate needs the notification it comes from; the app shows that source next to the rate."/>)}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap' }}>
        <Input.Search allowClear placeholder="Code or description" onSearch={(value) => setQuery(value.trim())} style={{ maxWidth: 360 }}/>
        <span style={{ flex: 1 }}/>
        <input ref={fileInput} type="file" accept=".csv,text/csv" hidden onChange={(event) => void pick(event.target.files?.[0]).finally(() => (event.target.value = ''))}/>
        <Button icon={<UploadOutlined />} onClick={() => fileInput.current?.click()}>
          Import CSV
        </Button>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setAdding(true)}>
          Add code
        </Button>
      </div>

      {preview && (<Panel title={`Import ${preview.fileName}`}>
          <Alert type={preview.rejected ? 'warning' : 'success'} showIcon title={`${preview.rows} rows: ${preview.accepted} ready to import, ${preview.rejected} left out`} description="Columns: HSN/SAC, Type, Description, Taxability, GST Rate, Cess, From Date, Notification. One row is one rate period; a code with several periods takes several rows." action={<span style={{ display: 'flex', gap: 8 }}>
                <Button onClick={() => setPreview(null)}>Cancel</Button>
                <Button type="primary" onClick={runImport} loading={busy} disabled={!preview.accepted}>
                  Import {preview.accepted}
                </Button>
              </span>}/>
          {preview.errors.length > 0 && (<Table style={{ marginTop: 12 }} size="small" rowKey={(row) => `${row.row}`} dataSource={preview.errors} pagination={{ pageSize: 20, hideOnSinglePage: true }} columns={[
                    { title: 'Row', dataIndex: 'row', width: 70 },
                    { title: 'Code', dataIndex: 'code', width: 120, render: (value) => <span className="mono">{value}</span> },
                    { title: 'Why it was left out', dataIndex: 'message' }
                ]}/>)}
        </Panel>)}

      <ErrorNotice error={list.error}/>
      <Panel flush>
        <DataTable rowKey="code" loading={list.loading} dataSource={list.data?.codes ?? []} pagination={{ pageSize: 50, hideOnSinglePage: true }} scroll={{ x: 900 }} onRow={(row) => ({ onClick: () => setViewing(row.code), style: { cursor: 'pointer' } })} locale={{ emptyText: query ? 'No code matches.' : 'No codes yet.' }} columns={[
            { title: 'Code', dataIndex: 'code', width: 110, render: (value) => <span className="mono">{value}</span> },
            { title: 'Type', dataIndex: 'kind', width: 100, render: (value) => (value === 'services' ? 'SAC' : 'HSN') },
            { title: 'Description', dataIndex: 'description', skeleton: 'lines' },
            { title: 'Rate today', width: 150, render: (_value, row) => (row.current ? <Tag color={row.current.taxability === 'taxable' ? 'blue' : 'default'}>{rateLabel(row.current)}</Tag> : <span className="cell-sub">None in force</span>) },
            { title: 'Since', width: 120, render: (_value, row) => (row.current ? formatDate(row.current.fromDate) : '—') },
            { title: 'Periods', width: 90, align: 'right', render: (_value, row) => row.rates.length }
        ]}/>
      </Panel>

      <Modal title="Add HSN / SAC" open={adding} onCancel={() => setAdding(false)} onOk={() => codeForm.submit()} okText="Save code" confirmLoading={busy} destroyOnHidden>
        <Form form={codeForm} layout="vertical" requiredMark={false} onFinish={saveCode} initialValues={{ kind: 'goods' }}>
          <div className="form-row">
            <Form.Item label="Type" name="kind">
              <Select placeholder="Choose HSN or SAC" options={KIND_OPTIONS}/>
            </Form.Item>
            <Form.Item label="Code" name="code" normalize={(value) => value.replace(/\D/g, '')} rules={[{ required: true, message: 'Enter the code.' }]} extra="HSN: 2, 4, 6 or 8 digits. SAC: 6 digits from 99.">
              <Input placeholder="998431" className="mono" maxLength={8} autoFocus autoComplete="off"/>
            </Form.Item>
          </div>
          <Form.Item label="Description" name="description" rules={[{ required: true, whitespace: true, max: 1000, message: 'Enter the description as in the schedule.' }]}>
            <Input.TextArea placeholder="Online information and database access services" rows={3}/>
          </Form.Item>
        </Form>
      </Modal>

      <Drawer title={entry ? `${entry.kind === 'services' ? 'SAC' : 'HSN'} ${entry.code}` : viewing ?? ''} open={viewing !== null} onClose={() => setViewing(null)} size={620} destroyOnHidden>
        <ErrorNotice error={detail.error}/>
        {entry && (<>
            <p style={{ marginTop: 0 }}>{entry.description}</p>
            <p className="cell-sub">
              Updated by {entry.updatedBy ?? 'system'} · {formatDateTime(entry.updatedAt)}
            </p>
            <h4 style={{ margin: '16px 0 8px' }}>Rates</h4>
            <Table size="small" rowKey="fromDate" pagination={false} dataSource={entry.rates} locale={{ emptyText: 'No rate yet. Add the rate below.' }} columns={[
                { title: 'From', dataIndex: 'fromDate', width: 110, render: (value) => formatDate(value) },
                { title: 'To', dataIndex: 'toDate', width: 110, render: (value) => (value ? formatDate(value) : 'In force') },
                { title: 'Rate', width: 120, render: (_value, row) => rateLabel(row) },
                { title: 'Source', dataIndex: 'source' },
                {
                    title: '',
                    width: 44,
                    render: (_value, row) => (<Popconfirm title="Remove this rate period?" okText="Remove" okButtonProps={{ danger: true }} onConfirm={() => removeRate(row)}>
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} aria-label={`Remove rate from ${row.fromDate}`}/>
                    </Popconfirm>)
                }
            ]}/>
            <h4 style={{ margin: '20px 0 8px' }}>Add a rate period</h4>
            <Form form={rateForm} layout="vertical" requiredMark={false} onFinish={saveRate} initialValues={{ taxability: 'taxable', gstRate: '', cessRate: '' }}>
              <div className="form-row">
                <Form.Item label="From" name="fromDate" rules={[{ required: true, message: 'Choose the date it applies from.' }]}>
                  <DatePicker placeholder="DD-MM-YYYY" format="DD-MM-YYYY" style={{ width: '100%' }}/>
                </Form.Item>
                <Form.Item label="Taxability" name="taxability">
                  <Select placeholder="Choose taxability" options={Object.keys(TAXABILITY).map((value) => ({ value, label: TAXABILITY[value] }))}/>
                </Form.Item>
              </div>
              {taxability === 'taxable' && (<div className="form-row">
                  <Form.Item label="GST rate %" name="gstRate" rules={[{ required: true, pattern: /^\d{1,3}(\.\d{1,3})?$/, message: 'A rate like 18 or 0.25.' }]}>
                    <Input placeholder="18" inputMode="decimal"/>
                  </Form.Item>
                  <Form.Item label="Cess %" name="cessRate" rules={[{ pattern: /^(\d{1,3}(\.\d{1,3})?)?$/, message: 'A rate like 12, or blank.' }]}>
                    <Input placeholder="0" inputMode="decimal"/>
                  </Form.Item>
                </div>)}
              <Form.Item label="Source" name="source" rules={[{ required: true, whitespace: true, max: 300, message: 'Name the notification the rate comes from.' }]} extra="The notification number and schedule entry, as published by CBIC.">
                <Input placeholder="Notification 01/2017-CT (Rate)" />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={busy}>
                Save rate
              </Button>
            </Form>
          </>)}
      </Drawer>
    </>);
}
